import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2, Globe } from "lucide-react";
import SEO from "@/components/SEO";
import Section from "@/components/Section";
import { fetchPage, fetchSiteSettings, postContact } from "@/lib/api";

export default function Contact() {
  const [params] = useSearchParams();
  const [page, setPage] = useState(null);
  const [s, setS] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: params.get("subject") || "",
    message: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") window.__prerender_ready = false;
    fetchPage("contact").then(setPage).catch(() => {});
    fetchSiteSettings().then(setS).catch(() => {});
  }, []);

  useEffect(() => {
    if (page?.meta_description) {
      const t = setTimeout(() => {
        if (typeof window !== "undefined") window.__prerender_ready = true;
      }, 50);
      return () => clearTimeout(t);
    }
  }, [page?.meta_description]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your name");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error("Enter a valid email");
    if (form.subject.trim().length < 2) return toast.error("Please add a subject");
    if (form.message.trim().length < 10) return toast.error("Message should be at least 10 characters");
    setSubmitting(true);
    try {
      await postContact(form);
      toast.success("Message sent. The AOA Somalia Secretariat will be in touch.");
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to send";
      toast.error(typeof msg === "string" ? msg : "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  const intro = (page?.sections || []).find((x) => x.type === "hero_compact");

  return (
    <>
      <SEO
        title={page?.title || "Contact"}
        description={page?.meta_description}
        ogImage={page?.og_image}
        path="/contact"
      />
      {intro && <Section section={intro} pageTitle={page?.title} />}

      <section className="bg-[hsl(var(--aoa-gray-bg))]" data-testid="contact-section">
        <div className="aoa-container py-20">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr]">
            {/* Form */}
            <motion.form
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              onSubmit={onSubmit}
              className="rounded-2xl border border-slate-200 bg-white p-8 md:p-10 shadow-sm"
              data-testid="contact-form"
            >
              <h2 className="aoa-h2 text-[hsl(var(--aoa-navy))]">Send us a message</h2>
              <p className="mt-2 text-sm text-slate-600">
                The Secretariat reads every message and will respond during working hours.
              </p>

              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <Field label="Full Name" required>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name"
                    required
                    minLength={2}
                    className={inputCls}
                    placeholder="Your name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email"
                    required
                    className={inputCls}
                    placeholder="name@organization.com"
                  />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Subject" required>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={update("subject")}
                    data-testid="contact-subject"
                    required
                    className={inputCls}
                    placeholder="How can we help?"
                  />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Message" required>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-message"
                    required
                    minLength={10}
                    rows={6}
                    className={`${inputCls} resize-none`}
                    placeholder="Briefly describe your enquiry..."
                  />
                </Field>
              </div>

              {/* Honeypot — visually hidden */}
              <div aria-hidden="true" className="hidden">
                <label>
                  Website
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                    name="website"
                  />
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-8 aoa-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Message <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </motion.form>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="space-y-6"
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="contact-details-card">
                <h3 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">
                  Headquarters
                </h3>
                <div className="mt-5 space-y-5 text-sm">
                  <Detail icon={<MapPin className="h-4 w-4" />} label="Address">
                    {s?.contact?.address}
                  </Detail>
                  <Detail icon={<Mail className="h-4 w-4" />} label="Email">
                    <a
                      href={`mailto:${s?.contact?.email}`}
                      className="text-[hsl(var(--aoa-royal))] hover:underline"
                      data-testid="contact-email-link"
                    >
                      {s?.contact?.email}
                    </a>
                  </Detail>
                  <Detail icon={<Phone className="h-4 w-4" />} label="Telephones">
                    <div className="space-y-1">
                      {(s?.contact?.phones || []).map((p) => (
                        <a
                          key={p}
                          href={`tel:${p.replace(/\s/g, "")}`}
                          className="block text-slate-700 hover:text-[hsl(var(--aoa-royal))]"
                        >
                          {p}
                        </a>
                      ))}
                    </div>
                  </Detail>
                  <Detail icon={<Globe className="h-4 w-4" />} label="Working Hours">
                    {s?.contact?.working_hours || "To be published"}
                  </Detail>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="contact-map">
                <iframe
                  title="Aden Adde International Airport — Mogadishu"
                  src={
                    s?.contact?.map_embed ||
                    "https://www.google.com/maps?q=Aden+Adde+International+Airport+Mogadishu&output=embed"
                  }
                  loading="lazy"
                  className="w-full h-72 border-0"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "block w-full rounded-xl border border-slate-200 bg-[hsl(var(--aoa-gray-bg))] px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-[hsl(var(--aoa-royal))] focus:bg-white";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-2 inline-block text-xs font-semibold uppercase tracking-[0.14em] text-[hsl(var(--aoa-navy))]">
        {label}
        {required && <span className="text-[hsl(var(--aoa-royal))]"> *</span>}
      </span>
      {children}
    </label>
  );
}

function Detail({ icon, label, children }) {
  return (
    <div className="flex gap-3">
      <div className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-[hsl(var(--aoa-gray-bg))] text-[hsl(var(--aoa-royal))]">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
        <div className="mt-1 text-sm text-slate-700">{children}</div>
      </div>
    </div>
  );
}
